import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Calendar, Shield, Activity, BarChart, ArrowRight, Sparkles, Heart, Star, Users } from 'lucide-react';
import TrustBadges from '../components/ui/TrustBadges';
import TestimonialCarousel from '../components/ui/TestimonialCarousel';
import FeatureComparison from '../components/ui/FeatureComparison';
import SocialProof from '../components/ui/SocialProof';
import EmotionalConnection from '../components/ui/EmotionalConnection';

const Home: React.FC = () => {
  const { t } = useTranslation();

  const features = [
    {
      icon: <Calendar className="h-8 w-8 text-purple-600" />,
      title: t('home.features.feature1.title'),
      description: t('home.features.feature1.description'),
      gradient: 'from-purple-500 to-indigo-500',
      benefits: ['Accurate predictions', 'Smart reminders', 'Pattern recognition']
    },
    {
      icon: <Activity className="h-8 w-8 text-pink-600" />,
      title: t('home.features.feature2.title'),
      description: t('home.features.feature2.description'),
      gradient: 'from-pink-500 to-rose-500',
      benefits: ['Personalized insights', 'Health trends', 'AI recommendations']
    },
    {
      icon: <BarChart className="h-8 w-8 text-emerald-600" />,
      title: t('home.features.feature3.title'),
      description: t('home.features.feature3.description'),
      gradient: 'from-emerald-500 to-teal-500',
      benefits: ['Multilingual support', 'Expert content', 'Cultural sensitivity']
    },
    {
      icon: <Shield className="h-8 w-8 text-blue-600" />,
      title: 'Privacy & Security',
      description: 'Your data is encrypted and secure, with complete privacy controls.',
      gradient: 'from-blue-500 to-cyan-500',
      benefits: ['End-to-end encryption', 'HIPAA compliant', 'Your data, your control']
    },
  ];

  return (
    <div className="space-y-20 py-8">
      {/* Hero Section - Enhanced with emotional appeal */}
      <section className="text-center relative overflow-hidden">
        <motion.div
          className="absolute inset-0 -z-10"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
        >
          <div className="absolute top-20 left-10 w-72 h-72 bg-purple-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
          <div className="absolute top-40 right-10 w-72 h-72 bg-pink-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
          <div className="absolute -bottom-8 left-20 w-72 h-72 bg-indigo-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="max-w-4xl mx-auto relative z-10"
        >
          <motion.div
            className="inline-flex items-center space-x-2 bg-white/80 backdrop-blur-sm border border-purple-200 rounded-full px-4 py-2 mb-6"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
          >
            <Sparkles className="w-4 h-4 text-purple-600" />
            <span className="text-sm font-medium text-purple-700">Trusted by 50,000+ Women Worldwide</span>
          </motion.div>

          <motion.h1 
            className="text-5xl md:text-6xl font-bold mb-6 leading-tight"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
          >
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-600 via-pink-500 to-indigo-600">
              {t('home.hero.title1')}
            </span>
            <br />
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500">
              {t('home.hero.title2')}
            </span>
          </motion.h1>
          
          <motion.p 
            className="text-xl text-gray-700 mb-8 leading-relaxed max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.8 }}
          >
            {t('home.hero.subtitle')}
          </motion.p>

          {/* Social proof in hero */}
          <motion.div
            className="flex items-center justify-center space-x-6 mb-8 text-sm text-gray-600"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.8 }}
          >
            <div className="flex items-center space-x-2">
              <div className="flex -space-x-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                ))}
              </div>
              <span>4.9/5 rating</span>
            </div>
            <div className="flex items-center space-x-2">
              <Users className="w-4 h-4 text-purple-500" />
              <span>50,000+ users</span>
            </div>
            <div className="flex items-center space-x-2">
              <Heart className="w-4 h-4 text-pink-500" />
              <span>98% satisfaction</span>
            </div>
          </motion.div>
          
          <motion.div 
            className="flex flex-col sm:flex-row justify-center gap-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.8 }}
          >
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Link
                to="/register"
                className="group inline-flex items-center space-x-2 px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-500 text-white font-semibold rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <span>Start Your Journey Free</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Link
                to="/login"
                className="inline-flex items-center space-x-2 px-8 py-4 bg-white/80 backdrop-blur-sm text-purple-600 font-semibold rounded-2xl shadow-lg hover:shadow-xl border border-purple-200 transition-all duration-300"
              >
                <span>See How It Works</span>
              </Link>
            </motion.div>
          </motion.div>

          {/* Trust indicators */}
          <motion.div
            className="mt-8 flex items-center justify-center space-x-8 text-xs text-gray-500"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9 }}
          >
            <div className="flex items-center space-x-2">
              <Shield className="w-4 h-4 text-green-500" />
              <span>HIPAA Compliant</span>
            </div>
            <div className="flex items-center space-x-2">
              <Heart className="w-4 h-4 text-pink-500" />
              <span>Medically Reviewed</span>
            </div>
            <div className="flex items-center space-x-2">
              <Users className="w-4 h-4 text-blue-500" />
              <span>Community Driven</span>
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* Trust Badges */}
      <section>
        <TrustBadges />
      </section>

      {/* Social Proof Section */}
      <section>
        <motion.div 
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Trusted by Women Everywhere</h2>
          <p className="text-lg text-gray-600">Join a global community of empowered women</p>
        </motion.div>
        <SocialProof />
      </section>

      {/* Emotional Connection Section */}
      <section>
        <EmotionalConnection />
      </section>

      {/* Features Section - Enhanced with benefits */}
      <section>
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <motion.div
            className="inline-block bg-gradient-to-r from-purple-100 to-pink-100 rounded-full px-4 py-2 mb-4"
            initial={{ scale: 0 }}
            whileInView={{ scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
          >
            <span className="text-sm font-medium text-purple-700">{t('home.features.overline')}</span>
          </motion.div>
          <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('home.features.title')}</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('home.features.subtitle')}
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              className="group relative bg-white/80 backdrop-blur-sm p-8 rounded-2xl shadow-lg border border-white/20 hover:shadow-2xl transition-all duration-500"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              whileHover={{ y: -8, scale: 1.02 }}
            >
              <motion.div 
                className={`inline-flex p-4 bg-gradient-to-br ${feature.gradient} rounded-2xl mb-6 shadow-lg`}
                whileHover={{ rotate: 10, scale: 1.1 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <div className="text-white">
                  {feature.icon}
                </div>
              </motion.div>
              <h3 className="text-2xl font-bold mb-4 text-gray-900 group-hover:text-purple-700 transition-colors">
                {feature.title}
              </h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                {feature.description}
              </p>
              
              {/* Benefits list */}
              <div className="space-y-2">
                {feature.benefits.map((benefit, i) => (
                  <motion.div
                    key={i}
                    className="flex items-center space-x-2 text-sm text-gray-600"
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.3 + i * 0.1 }}
                  >
                    <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                    <span>{benefit}</span>
                  </motion.div>
                ))}
              </div>

              <motion.div
                className="absolute bottom-0 left-0 h-1 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full"
                initial={{ width: 0 }}
                whileInView={{ width: "100%" }}
                viewport={{ once: true }}
                transition={{ delay: 0.5 + index * 0.1, duration: 0.8 }}
              />
            </motion.div>
          ))}
        </div>
      </section>

      {/* Testimonials Section */}
      <section>
        <TestimonialCarousel />
      </section>

      {/* Feature Comparison Section */}
      <section>
        <FeatureComparison />
      </section>

      {/* CTA Section - Enhanced with urgency and social proof */}
      <motion.section 
        className="relative overflow-hidden"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-purple-600 via-pink-500 to-indigo-600 rounded-3xl"></div>
        <div className="absolute inset-0 bg-black/10 rounded-3xl"></div>
        <motion.div 
          className="relative p-12 md:p-16 text-white text-center rounded-3xl"
          initial={{ y: 30 }}
          whileInView={{ y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <motion.div
            className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-sm rounded-full px-4 py-2 mb-6"
            initial={{ scale: 0 }}
            whileInView={{ scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
          >
            <Heart className="w-4 h-4 text-pink-200" />
            <span className="text-sm font-medium">Join 50,000+ Women Today</span>
          </motion.div>

          <motion.h2 
            className="text-4xl font-bold mb-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
          >
            {t('home.cta.title')}
          </motion.h2>
          <motion.p 
            className="text-xl mb-8 max-w-2xl mx-auto opacity-90"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.5 }}
          >
            {t('home.cta.subtitle')}
          </motion.p>

          {/* Social proof in CTA */}
          <motion.div
            className="flex items-center justify-center space-x-6 mb-8 text-sm opacity-90"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.6 }}
          >
            <div className="flex items-center space-x-2">
              <div className="flex -space-x-1">
                {['👩🏻‍💼', '👩🏽‍⚕️', '👩🏼‍🎓', '👩🏿‍💻'].map((avatar, i) => (
                  <div key={i} className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center border border-white/30">
                    <span className="text-xs">{avatar}</span>
                  </div>
                ))}
              </div>
              <span>Sarah, Maria, Emma & 49,997 others joined this week</span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.7 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Link
              to="/register"
              className="group inline-flex items-center space-x-2 px-8 py-4 bg-white text-purple-600 font-semibold rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <span>{t('home.cta.button')}</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>

          <motion.div
            className="mt-6 text-sm opacity-75"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.8 }}
          >
            <p>✨ Free forever • No credit card required • Start in 30 seconds</p>
          </motion.div>
        </motion.div>
      </motion.section>
    </div>
  );
};

export default Home;